/** Breathing room kept between a popover and the viewport edge, in px. */
export const POPOVER_VIEWPORT_MARGIN = 8;

/**
 * Keeps a `position: fixed` popover horizontally inside the viewport.
 *
 * A portaled popover is normally placed at its trigger's left edge, which is fine everywhere the
 * trigger has room to its right — and silently off-screen where it does not. The Lead Panel is the
 * case that bites: it is `fixed right-0 w-[450px]`, flush against the viewport's right edge, so a
 * trigger sitting ~300px into it leaves ~150px of room for a 260px calendar. The popover renders,
 * nothing clips it and nothing errors; it is simply painted past the edge of the screen, which
 * reads to the user as "the calendar does not open" (PREC-4130).
 *
 * Note this needs the popover's **measured** width, not its trigger's: the two are unrelated, and a
 * popover whose width comes from a `min-w-*` class or its own content is only knowable after it has
 * rendered. Vertical flipping is each caller's own business — the axis that overflows depends on
 * where the trigger sits, and only the horizontal rule is shared.
 *
 * When the popover is wider than the viewport it can fit nowhere; pin it to the left margin so at
 * least its leading edge is reachable.
 */
export const clampPopoverLeft = (
	anchorLeft: number,
	popoverWidth: number,
	viewportWidth: number,
	margin: number = POPOVER_VIEWPORT_MARGIN
): number => {
	const maxLeft = viewportWidth - popoverWidth - margin;

	if (maxLeft <= margin) {
		return margin;
	}

	return Math.max(margin, Math.min(anchorLeft, maxLeft));
};
